import { Client, MacroRange as ClientMacroRange } from '@/types/client';

export type ResolvedClientGoals = {
  calories?: number;
  protein?: number;
  proteinMin?: number;
  proteinMax?: number;
  carbs?: number;
  carbsMin?: number;
  carbsMax?: number;
  fat?: number;
  fatMin?: number;
  fatMax?: number;
  fiber?: number;
};

export type PlanGoalsInput = Partial<{
  calories: number | null;
  protein: number | null;
  proteinMin: number | null;
  proteinMax: number | null;
  carbs: number | null;
  carbsMin: number | null;
  carbsMax: number | null;
  fat: number | null;
  fatMin: number | null;
  fatMax: number | null;
  fiber: number | null;
}>;

export type OverrideGoalsInput = Partial<{
  eerGoalCalories: number | null;
  proteinGoalMin: number | null;
  proteinGoalMax: number | null;
  carbsGoalMin: number | null;
  carbsGoalMax: number | null;
  fatGoalMin: number | null;
  fatGoalMax: number | null;
  fiberGoalGrams: number | null;
}>;

export type ResolveClientGoalsOptions = {
  clientDetails?: Client | null;
  planGoals?: PlanGoalsInput | null;
  overrideGoals?: OverrideGoalsInput | null;
  /**
   * Controls when client macrosRanges should be used as a fallback.
   * - 'always': always consider macrosRanges (default, matches finalized page behaviour)
   * - 'whenClientGoalMissing': only consider macrosRanges if clientGoal is absent (matches draft behaviour)
   * - 'never': never fall back to macrosRanges
   */
  macroRangeFallback?: 'always' | 'whenClientGoalMissing' | 'never';
};

type RangeResult = { min?: number; max?: number };

type GoalValueObject = {
  value?: number | string | null;
  grams?: { value?: number | string | null } | null;
  kcal?: { value?: number | string | null } | null;
};

type UnknownRecord = Record<string, unknown>;

type ClientTargetCaloriesSource = Client & {
  conditionAdjustedTargetCalories?: number | string | null;
  eerGoalCalories?: number | string | null;
  finalTargetCalories?: number | string | null;
  maintenanceEER?: number | string | null;
  targetCalories?: number | string | null;
};

const normalizeNutrientId = (value: unknown): string =>
  typeof value === 'string' ? value.replace(/[_\s-]/g, '').toLowerCase() : '';

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const pickNumber = (...values: Array<unknown>): number | undefined => {
  for (const value of values) {
    const parsed = toFiniteNumber(value);
    if (parsed !== undefined) {
      return parsed;
    }
  }
  return undefined;
};

const pickPositiveNumber = (...values: Array<unknown>): number | undefined => {
  for (const value of values) {
    const parsed = toFiniteNumber(value);
    if (parsed !== undefined && parsed > 0) return parsed;
  }
  return undefined;
};

const asRecord = (value: unknown): UnknownRecord | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;

const averageFromRange = (min?: number, max?: number): number | undefined => {
  if (typeof min === 'number' && typeof max === 'number') {
    return (min + max) / 2;
  }
  return min ?? max ?? undefined;
};

const normalizeNumber = (value?: number | null): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const extractGoalValue = (input: unknown): number | undefined => {
  const direct = toFiniteNumber(input);
  if (direct !== undefined) {
    return direct;
  }

  if (!input || typeof input !== 'object') {
    return undefined;
  }

  const goalValue = input as GoalValueObject;
  return pickNumber(goalValue.grams?.value, goalValue.value, goalValue.kcal?.value);
};

export const resolveClientTargetCalories = (clientDetails: Client | null | undefined): number | undefined => {
  if (!clientDetails) return undefined;
  const source = clientDetails as ClientTargetCaloriesSource;
  const clientGoals = asRecord(source.clientGoals);
  const clinicalRules = asRecord(clientGoals?.clinicalRules);
  const resolvedTargets = asRecord(clinicalRules?.resolvedTargets);
  const legacyClientGoal = asRecord((source as unknown as { clientGoal?: unknown }).clientGoal);

  return pickPositiveNumber(
    source.finalTargetCalories,
    source.targetCalories,
    source.conditionAdjustedTargetCalories,
    resolvedTargets?.finalTargetCalories,
    resolvedTargets?.targetCalories,
    resolvedTargets?.conditionAdjustedTargetCalories,
    extractGoalValue(clientGoals?.calories),
    extractGoalValue(clientGoals?.dailyEnergyExpenditure),
    clientGoals?.targetCalories,
    clientGoals?.eerGoalCalories,
    legacyClientGoal?.eerGoalCalories,
    source.eerGoalCalories,
    source.eerOverride,
    source.eerCalories,
    source.maintenanceEER,
    source.eer,
  );
};

const extractPlanGoalValue = (planGoals: PlanGoalsInput | null | undefined, key: string): number | undefined => {
  if (!planGoals || typeof planGoals !== 'object') return undefined;
  const goalMap = planGoals as Record<string, unknown>;
  return extractGoalValue(goalMap[key]);
};

const extractDrvRda = (list: unknown, nutrientIdCandidates: string[]): number | undefined => {
  if (!Array.isArray(list) || nutrientIdCandidates.length === 0) {
    return undefined;
  }

  const normalizedCandidates = nutrientIdCandidates.map((candidate) => normalizeNutrientId(candidate));
  const matched = list.find((item) => {
    if (!item || typeof item !== 'object') return false;
    const nutrientId = normalizeNutrientId((item as { nutrientId?: unknown }).nutrientId);
    return normalizedCandidates.includes(nutrientId);
  }) as { rda?: unknown } | undefined;

  return toFiniteNumber(matched?.rda);
};

export const resolveClientGoals = ({
  clientDetails,
  planGoals,
  overrideGoals,
  macroRangeFallback = 'always',
}: ResolveClientGoalsOptions): ResolvedClientGoals | null => {
  if (!clientDetails && !planGoals && !overrideGoals) {
    return null;
  }

  const clientGoal = clientDetails?.clientGoal ?? null;
  const macrosRanges = clientDetails?.macrosRanges ?? null;
  const structuredClientGoals = (
    (clientDetails as unknown as { clientGoals?: Record<string, unknown> | null })?.clientGoals ?? null
  ) as Record<string, unknown> | null;
  const drvMacros = clientDetails?.drvRequirements?.macronutrients;
  const drvProteinRda = extractDrvRda(drvMacros?.proteins, ['protein']);
  const drvCarbsRda = extractDrvRda(drvMacros?.carbohydrates, ['carbohydrate', 'carbohydrates']);
  const drvFatRda = extractDrvRda(drvMacros?.lipids, ['total_fat', 'fat', 'lipid']);
  const drvFiberRda = extractDrvRda(drvMacros?.carbohydrates, ['dietary_fibre', 'dietary_fiber', 'fiber']);

  const shouldUseMacroRanges =
    macroRangeFallback === 'always' ||
    (macroRangeFallback === 'whenClientGoalMissing' && !clientGoal);

  const resolveRange = ({
    overrideMin,
    overrideMax,
    clientMin,
    clientMax,
    macroRange,
    planMin,
    planMax,
  }: {
    overrideMin?: number | null;
    overrideMax?: number | null;
    clientMin?: number | null;
    clientMax?: number | null;
    macroRange?: ClientMacroRange | null;
    planMin?: number | null;
    planMax?: number | null;
  }): RangeResult => {
    const macroMin = shouldUseMacroRanges ? macroRange?.min : undefined;
    const macroMax = shouldUseMacroRanges ? macroRange?.max : undefined;

    return {
      min: normalizeNumber(pickNumber(overrideMin, clientMin, macroMin, macroMax, planMin, planMax)),
      max: normalizeNumber(pickNumber(overrideMax, clientMax, macroMax, macroMin, planMax, planMin)),
    };
  };

  const resolveMacroValue = (
    range: RangeResult,
    macroRange?: ClientMacroRange | null,
    ...fallbacks: Array<number | null | undefined>
  ): number | undefined => {
    const average = averageFromRange(range.min, range.max);
    if (typeof average === 'number') {
      return average;
    }

    if (shouldUseMacroRanges) {
      const macroValue = pickNumber(macroRange?.min, macroRange?.max);
      if (typeof macroValue === 'number') {
        return macroValue;
      }
    }

    return pickNumber(...fallbacks);
  };

  const proteinRange = resolveRange({
    overrideMin: overrideGoals?.proteinGoalMin,
    overrideMax: overrideGoals?.proteinGoalMax,
    clientMin: clientGoal?.proteinGoalMin ?? null,
    clientMax: clientGoal?.proteinGoalMax ?? null,
    macroRange: macrosRanges?.protein ?? null,
    planMin: planGoals?.proteinMin ?? null,
    planMax: planGoals?.proteinMax ?? null,
  });

  const carbRange = resolveRange({
    overrideMin: overrideGoals?.carbsGoalMin,
    overrideMax: overrideGoals?.carbsGoalMax,
    clientMin: clientGoal?.carbsGoalMin ?? null,
    clientMax: clientGoal?.carbsGoalMax ?? null,
    macroRange: macrosRanges?.carbs ?? null,
    planMin: planGoals?.carbsMin ?? null,
    planMax: planGoals?.carbsMax ?? null,
  });

  const fatRange = resolveRange({
    overrideMin: overrideGoals?.fatGoalMin,
    overrideMax: overrideGoals?.fatGoalMax,
    clientMin: clientGoal?.fatGoalMin ?? null,
    clientMax: clientGoal?.fatGoalMax ?? null,
    macroRange: macrosRanges?.fat ?? null,
    planMin: planGoals?.fatMin ?? null,
    planMax: planGoals?.fatMax ?? null,
  });

  const calories = pickNumber(
    overrideGoals?.eerGoalCalories,
    extractPlanGoalValue(planGoals, 'calories'),
    extractPlanGoalValue(planGoals, 'dailyEnergyExpenditure'),
    resolveClientTargetCalories(clientDetails),
    clientGoal?.eerGoalCalories,
    planGoals?.calories
  );

  const fiberValue = pickNumber(
    overrideGoals?.fiberGoalGrams,
    clientGoal?.fiberGoalGrams,
    shouldUseMacroRanges ? macrosRanges?.fiber?.min : undefined,
    shouldUseMacroRanges ? macrosRanges?.fiber?.max : undefined,
    extractPlanGoalValue(planGoals, 'fiber'),
    extractPlanGoalValue(planGoals, 'dietaryFibre'),
    extractPlanGoalValue(planGoals, 'dietaryFiber'),
    extractGoalValue(structuredClientGoals?.dietaryFibre),
    extractGoalValue(structuredClientGoals?.dietaryFiber),
    drvFiberRda,
    planGoals?.fiber
  );

  const proteinValue = resolveMacroValue(
    proteinRange,
    macrosRanges?.protein,
    extractPlanGoalValue(planGoals, 'protein'),
    extractGoalValue(structuredClientGoals?.protein),
    clientGoal?.proteinGoalMin,
    clientGoal?.proteinGoalMax,
    drvProteinRda
  );

  const carbValue = resolveMacroValue(
    carbRange,
    macrosRanges?.carbs,
    extractPlanGoalValue(planGoals, 'carbs'),
    extractPlanGoalValue(planGoals, 'carbohydrates'),
    extractGoalValue(structuredClientGoals?.carbohydrates),
    clientGoal?.carbsGoalMin,
    clientGoal?.carbsGoalMax,
    drvCarbsRda
  );

  const fatValue = resolveMacroValue(
    fatRange,
    macrosRanges?.fat,
    extractPlanGoalValue(planGoals, 'fat'),
    extractPlanGoalValue(planGoals, 'totalFat'),
    extractGoalValue(structuredClientGoals?.totalFat),
    clientGoal?.fatGoalMin,
    clientGoal?.fatGoalMax,
    drvFatRda
  );

  return {
    calories,
    protein: proteinValue,
    proteinMin: proteinRange.min,
    proteinMax: proteinRange.max,
    carbs: carbValue,
    carbsMin: carbRange.min,
    carbsMax: carbRange.max,
    fat: fatValue,
    fatMin: fatRange.min,
    fatMax: fatRange.max,
    fiber: normalizeNumber(fiberValue),
  };
};
